import { lazy, Suspense } from "react";
import HomeHero from "@/components/home/HomeHero";
import ProofBar from "@/components/home/ProofBar";
import SEO from "@/components/SEO";

// Lazy-load below-fold sections to reduce initial JS bundle
const ProblemSection = lazy(() => import("@/components/home/ProblemSection"));
const WhatYouGet = lazy(() => import("@/components/home/WhatYouGet"));
const InsightsTeaser = lazy(() => import("@/components/home/InsightsTeaser"));
const HowItWorksTeaser = lazy(() => import("@/components/home/HowItWorksTeaser"));
const PricingTeaser = lazy(() => import("@/components/home/PricingTeaser"));
const ExclusionsSection = lazy(() => import("@/components/home/ExclusionsSection"));
const FAQ = lazy(() => import("@/components/FAQ"));
const HomeCTA = lazy(() => import("@/components/home/HomeCTA"));
import RelatedLinks from "@/components/RelatedLinks";

const Index = () => (
  <>
    <SEO
      title="Website Refresh for Service Businesses | Ezsy Webs"
      description="Ezsy Webs refreshes outdated contractor, law firm, and clinic websites into fast, lead-generating sites. Fixed pricing, 14-day turnaround, SEO preserved. Get a free homepage preview."
      path="/"
      image="/og-image.png"
      breadcrumbs={[{ name: "Home", path: "/" }]}
      jsonLd={[
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Ezsy Webs",
          "url": "https://www.ezsywebs.com",
          "description": "Ezsy Webs refreshes outdated contractor, law firm, and clinic websites into fast, lead-generating sites. Fixed pricing, 14-day turnaround. Get a free preview today.",
          "publisher": {
            "@type": "Organization",
            "@id": "https://www.ezsywebs.com/#organization",
            "name": "Ezsy Webs"
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.ezsywebs.com/apply",
            "name": "Get a Free Homepage Preview"
          }
        },
        {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://www.ezsywebs.com/#localbusiness",
          "name": "Ezsy Webs",
          "description": "Productized website refresh studio that transforms outdated service-business websites into fast, modern, lead-generating sites in 14 days.",
          "url": "https://www.ezsywebs.com",
          "image": "https://www.ezsywebs.com/og-image.png",
          "email": "hello@ezsywebs.com",
          "priceRange": "$1,900 - $4,200 CAD",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Toronto",
            "addressRegion": "ON",
            "addressCountry": "CA"
          },
          "areaServed": [
            { "@type": "Country", "name": "Canada" },
            { "@type": "Country", "name": "United States" }
          ],
          "sameAs": [
            "https://www.instagram.com/ezsywebs/",
            "https://x.com/EzsyWebs",
            "https://www.linkedin.com/company/ezsy-webs/",
            "https://www.facebook.com/profile.php?id=61566009779591"
          ]
        },
        {
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Website Refresh",
          "name": "Website Refresh",
          "description": "Fixed-price website refresh for service businesses. Layout update, performance optimization, SEO cleanup, and mobile responsiveness, delivered in 14 days.",
          "provider": {
            "@type": "Organization",
            "@id": "https://www.ezsywebs.com/#organization",
            "name": "Ezsy Webs",
            "url": "https://www.ezsywebs.com"
          },
          "areaServed": [
            { "@type": "Country", "name": "Canada" },
            { "@type": "Country", "name": "United States" }
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Website Refresh Packages",
            "itemListElement": [
              {
                "@type": "Offer",
                "name": "Starter Refresh",
                "description": "Up to 3 pages. Layout update, mobile optimization, SEO cleanup. 14-day turnaround.",
                "price": "1900",
                "priceCurrency": "CAD",
                "url": "https://www.ezsywebs.com/pricing",
                "availability": "https://schema.org/InStock"
              },
              {
                "@type": "Offer",
                "name": "Core Refresh",
                "description": "Up to 6 pages. Full layout update, performance optimization, SEO cleanup, tracking setup. 14-day turnaround.",
                "price": "2900",
                "priceCurrency": "CAD",
                "url": "https://www.ezsywebs.com/pricing",
                "availability": "https://schema.org/InStock"
              },
              {
                "@type": "Offer",
                "name": "Extended Refresh",
                "description": "7-10 pages. Full layout update, performance optimization, SEO cleanup, tracking setup. 14-day turnaround.",
                "price": "4200",
                "priceCurrency": "CAD",
                "url": "https://www.ezsywebs.com/pricing",
                "availability": "https://schema.org/InStock"
              }
            ]
          }
        }
      ]}
    />
    <HomeHero />
    <ProofBar />
    <Suspense fallback={null}>
      <ProblemSection />
      <WhatYouGet />
      <InsightsTeaser />
      <HowItWorksTeaser />
      <PricingTeaser />
      <ExclusionsSection />
      <FAQ />
      <RelatedLinks
        items={[
          { title: "How It Works", description: "See our 3-step refresh process from preview to launch.", to: "/how-it-works" },
          { title: "Pricing", description: "Fixed-price packages with no hidden fees or surprises.", to: "/pricing" },
          { title: "About Ezsy Webs", description: "Meet the team behind the website refresh studio.", to: "/about" },
        ]}
      />
      <HomeCTA />
    </Suspense>
  </>
);

export default Index;
